import React, { useState } from "react";
import styles from "styles/BulkUploadModal.module.css";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import DragAndDropUpload from "components/FileUploader/DragAndDropUpload";
import {
  Grid,
  Grid2,
  useTheme,
  Button,
  Box,
  Modal,
  Typography,
  Link,
} from "@mui/material";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import checkIcon from "assets/CheckIcon.png";
import fileIcon from "assets/fileIcon.svg";
import { useBulkUploadMutation } from "store/slices/apis/bulkUploadSlice";
import sampleFile from "assets/SampleFile/sample_excel_file.xlsx";
import { useParams } from "react-router-dom";

const BulkUploadModal = ({ bulkUploadModal, setBulkUploadModal }) => {
  const { projectId } = useParams();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkUpload] = useBulkUploadMutation();

  const openNotification = async (msg, type = "error") => {
    await dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const handleDrop = (acceptedFiles) => {
    const maxFileSize = 10 * 1024 * 1024;

    if (acceptedFiles[0].size > maxFileSize) {
      openNotification(
        acceptedFiles[0].size + "The selected file size should not be greater than 5MB."
      );
      return;
    }
    setSelectedFile(acceptedFiles[0]);
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
  };

  const handleUpload = async () => {
    setLoading(true);
    try {
      const response = await bulkUpload({
        file: selectedFile,
        projectId: projectId || null,
      }).unwrap();
      openNotification(
        response?.message ||
          "File uploaded successfully. Processing in background.",
        "success"
      );
      setSelectedFile(null);
      setLoading(false);
      setBulkUploadModal(false);
    } catch (error) {
        openNotification("File upload failed. Please try again.", "error");
        setLoading(false);
    }
  };

  const handleModalClose = () => {
    setBulkUploadModal(false);
    setSelectedFile(null);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = sampleFile;
    link.download = "sample-excel-file.xlsx";
    link.click();
  };

  return (
    <Modal
      open={bulkUploadModal}
      onClose={(e, reason) => {
        if (reason !== "backdropClick") {
          setBulkUploadModal(false);
        }
      }}
      className={styles.modalWrapper}
      sx={{
        "& .css-4nmryk-MuiBackdrop-root-MuiModal-backdrop": {
          opacity: "0 !important",
        },
      }}
    >
      <Box className={styles.modalContent}>
        <Box className={styles.modalHeader}>
          <Typography variant="h6" className={styles.modalTitle}>
            Bulk Upload User
          </Typography>
          <IconButton>
            <ClearIcon
              sx={{ fontSize: "25px", opacity: "0.5" }}
              onClick={() => handleModalClose()}
            />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <Typography className={styles.description}>
            You can upload XLS files. The max file size is 5 MB.
          </Typography>

          <Link
            onClick={handleDownload}
            sx={{
              color: theme?.customSettings?.themeColor,
              textDecoration: "underline",
              textUnderlinePosition: "under",
              marginLeft: "5px",
              cursor: "pointer",
            }}
          >
            Sample File
          </Link>
        </Box>

        <Box className={styles.uploadBox}>
          <DragAndDropUpload onDrop={handleDrop} fileType="excel" />
        </Box>
        {selectedFile && (
          <Box className={styles.fileInfo}>
            <Grid2 sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Grid sx={{
                height:"16px",
                width:"12px"
              }}>
                <img src={fileIcon} alt="" />
              </Grid>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#252525",
                }}
              >
                {selectedFile.name}
                {"    "}
                <span style={{ color: "#696969", marginLeft: "8px" }}>
                  {" "}
                  {(selectedFile.size / 1024).toFixed(2)} kb
                </span>
              </Typography>
            </Grid2>
            <Grid2
              sx={{
                height: "16px",
                width: "16px",
              }}
            >
              <img src={checkIcon} alt="" />
            </Grid2>
          </Box>
        )}

        <Box className={styles.buttonContainer}>
          <Grid2>
            {selectedFile && (
              <Button
                onClick={handleDeleteFile}
                sx={{
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: 500,
                  color: theme?.customSettings?.themeColor,
                  textTransform: "none",
                }}
              >
                Delete attachment(s)
              </Button>
            )}
          </Grid2>
          <Grid2>
            <Button
              variant="contained"
              sx={{
                backgroundColor: `${theme?.customSettings?.themeColor}`,
                color: "#FFF !important",
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: 500,
                padding: "8px 24px",
                textTransform: "none",
                "&.Mui-disabled": {
                  backgroundColor: `${theme?.customSettings?.themeColor}1F`,
                  color: "#FFF !important",
                },
              }}
              loading={loading}
              onClick={handleUpload}
              disabled={!selectedFile}
            >
              Upload
            </Button>
          </Grid2>
        </Box>
      </Box>
    </Modal>
  );
};

export default BulkUploadModal;
